import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Home, MapPin, PackageCheck, SkipForward, Truck } from 'lucide-react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { BulkResult } from '../ui/BulkResult';
import { ErrorState } from '../ui/ErrorState';
import { FilterableSelect } from '../ui/FilterableSelect';
import { ProgressBar } from '../ui/ProgressBar';
import { changeDeliveriesPickupPoint } from '../../features/deliveries/deliveriesThunks';
import { fetchPickupPoints } from '../../features/pickupPoints/pickupPointsThunks';
import { scheduleLabel } from '../../features/pickupPoints/pickupPointsUtils';
import { getFullAddress } from '../../features/orderDetails/orderUtils';
import { selectProgress } from '../../features/feedback/feedbackSlice';

const WHAT_HAPPENS = [
  {
    icon: MapPin,
    text: 'Cambia el destino de los envíos pendientes o por asignar. Los demás se omiten, y te decimos por qué.',
  },
  {
    icon: Home,
    text: 'La dirección del cliente se conserva: desde el detalle de la orden puedes volver a ella.',
  },
  {
    icon: Truck,
    text: 'Cada envío se vuelve a rutear con la dirección del punto, así que su courier puede cambiar según tus zonas.',
  },
  {
    icon: PackageCheck,
    text: 'Cuando el courier entregue quedará «Dejado en hub»; al escanearlo el punto pasará a «Recibido en hub», y se cerrará como Entregado cuando el punto se lo entregue al cliente.',
  },
  {
    icon: SkipForward,
    text: 'Se omiten los que ya van a ese punto y los que ya salieron.',
  },
];

const ROW_LIST = 'max-h-40 overflow-y-auto divide-y divide-border border border-border rounded-xl';

/** The order behind a row, or the shipment itself when the backend found none. */
const shipmentOf = (row) => (row.order_id ? `#${row.order_id}` : `Envío ${row.delivery_id}`);

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

const pointPlaceholder = (loading, options) => {
  if (loading) return 'Cargando puntos de retiro...';
  return options.length > 0 ? 'Elige el punto de retiro' : 'No tienes puntos de retiro';
};

const Section = ({ title, children }) => (
  <div className="flex flex-col gap-2">
    <h3 className="text-xs font-bold uppercase tracking-wider text-text-muted">{title}</h3>
    {children}
  </div>
);

const ChangeResult = ({ result, pointName, onClose }) => {
  const failureReasons = [...new Set(result.failed.map((row) => row.message))];

  return (
    <BulkResult
      result={{
        requested: result.requested,
        succeeded: result.redirected,
        skipped: result.skipped,
        failed: result.failed,
      }}
      succeededLabel="Cambiados"
      onClose={onClose}
    >
      {/* Its own scroll: with long lists the dialog outgrows a laptop screen,
          and the shared Modal does not scroll. */}
      <div className="flex flex-col gap-4 max-h-[45vh] overflow-y-auto pr-1">
        {result.redirected.length > 0 && (
          <p className="text-sm text-text-secondary text-center">
            {plural(result.redirected.length, 'envío va', 'envíos van')} a <b>{pointName}</b>. Verás
            el cambio en el historial en unos segundos, cuando cada uno se vuelva a rutear.
          </p>
        )}

        {failureReasons.length > 0 && (
          <ErrorState
            variant="inline"
            title={`${result.failed.length} sin cambiar`}
            description={`${failureReasons.join(' ')} Quedan seleccionados para que puedas reintentar.`}
          />
        )}

        {result.skipped.length > 0 && (
          <Section title="Omitidos">
            <ul className={ROW_LIST}>
              {result.skipped.map((row) => (
                <li
                  key={row.delivery_id}
                  data-testid="change-pickup-point-skipped"
                  data-reason={row.reason}
                  className="flex flex-col px-3 py-2"
                >
                  <span className="text-sm font-semibold text-ninja-dark dark:text-white">
                    {shipmentOf(row)}
                    {row.reference_id && (
                      <span className="font-normal text-text-secondary"> · {row.reference_id}</span>
                    )}
                  </span>
                  <span className="text-xs text-text-secondary">{row.message}</span>
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>
    </BulkResult>
  );
};

/**
 * Sends the selected shipments to one of the operator's pickup points, with
 * the rules of the order detail's «Entregar en punto de retiro».
 *
 * Only the admin workspace offers it: the points are the operator's, and it
 * is the operator who receives the parcels there. It ends on a tally instead
 * of closing, like «Cambiar courier», because the shipments skipped need the
 * operator to know why.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {number[]} props.deliveryIds
 * @param {(failedIds: number[]) => void} [props.onFinished] - Called when the
 *   result is closed, with the delivery ids that did not change, so the page
 *   can keep them selected for a retry.
 */
export const ChangePickupPointBulkModal = ({ isOpen, onClose, deliveryIds = [], onFinished }) => {
  const dispatch = useDispatch();
  const { items, loading, loaded, error: loadError } = useSelector((state) => state.pickupPoints);

  const [pickupPointId, setPickupPointId] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);
  // A selection over 100 goes out in batches; the thunk reports which one.
  const [requestId, setRequestId] = useState(null);
  const progress = useSelector(selectProgress(requestId));

  useEffect(() => {
    if (isOpen && !loaded) dispatch(fetchPickupPoints());
  }, [dispatch, isOpen, loaded]);

  const handleClose = () => {
    if (sending) return;
    const finished = result;
    setPickupPointId('');
    setError(null);
    setResult(null);
    onClose();
    if (finished) onFinished?.(finished.failed.map((row) => row.delivery_id));
  };

  const handleConfirm = async () => {
    setSending(true);
    setError(null);

    const running = dispatch(changeDeliveriesPickupPoint({ deliveryIds, pickupPointId }));
    setRequestId(running.requestId);

    try {
      setResult(await running.unwrap());
    } catch (err) {
      setError(typeof err === 'string' ? err : 'No se pudo cambiar el destino de los envíos');
    } finally {
      setSending(false);
    }
  };

  const count = deliveryIds.length;
  const options = items.map((point) => ({
    value: point.id,
    label: `${point.name} · ${getFullAddress(point.address)}`,
  }));
  const chosen = items.find((point) => point.id === pickupPointId);

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Enviar a punto de retiro"
      testId="change-pickup-point-modal"
      busy={sending}
    >
      {result ? (
        <ChangeResult result={result} pointName={chosen?.name || 'el punto de retiro'} onClose={handleClose} />
      ) : (
        <div className="flex flex-col gap-5">
          <p className="text-sm text-text-secondary text-center">
            Vas a enviar {plural(count, 'envío', 'envíos')} a un punto de retiro.
          </p>

          {loadError ? (
            <ErrorState
              variant="inline"
              title="No se pudieron cargar los puntos de retiro"
              description={loadError}
              onRetry={() => dispatch(fetchPickupPoints())}
            />
          ) : (
            <div>
              <FilterableSelect
                label="Punto de retiro"
                variant="form"
                value={pickupPointId}
                onChange={setPickupPointId}
                options={options}
                placeholder={pointPlaceholder(loading, options)}
                disabled={sending || options.length === 0}
                testId="change-pickup-point-select"
              />
              {chosen && (
                <p className="text-xs text-text-muted mt-2">Recepción: {scheduleLabel(chosen)}</p>
              )}
            </div>
          )}

          <ul className="flex flex-col gap-3">
            {WHAT_HAPPENS.map(({ icon: Icon, text }) => (
              <li key={text} className="flex items-start gap-2.5 text-sm text-text-secondary">
                <Icon size={16} className="text-text-muted flex-shrink-0 mt-0.5" />
                <span>{text}</span>
              </li>
            ))}
          </ul>

          {error && <ErrorState variant="inline" title="No se pudo cambiar" description={error} />}

          {sending && progress && (
            <ProgressBar
              value={progress.done}
              max={progress.total}
              label={progress.label}
              testId="bulk-progress"
            />
          )}

          <div className="flex items-center justify-end gap-3 mt-2">
            <Button variant="subtle" pill onClick={handleClose} disabled={sending}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              pill
              onClick={handleConfirm}
              loading={sending}
              loadingLabel="Cambiando..."
              disabled={count === 0 || !pickupPointId}
              data-testid="change-pickup-point-confirm"
            >
              Confirmar cambio
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
